/**
 * ThemeBridge
 *
 * Reads the CSS variable map stored in the box state and injects it as
 * inline CSS custom properties on a wrapper div.  Any descendant component
 * that uses var(--color-text-primary), var(--font-heading), var(--space-4)
 * etc. resolves correctly inside this subtree — no global stylesheet required.
 *
 * Also exposes the variables via React context so sub-components can read
 * individual tokens if needed (e.g. for JS-based calculations).
 *
 * Default variables (midnight-pro / dark indigo) are used as a fallback when
 * no theme has been loaded yet (e.g. during offline development or before the
 * first middleware call completes).
 */

import React, { createContext, useContext } from "react";
import { withCourseFont } from "../../../common/course_font";

/* eslint-disable react/prop-types */

// ─── Default theme tokens (light / editor fallback) ──────────────────────────
// Used when no theme has been fetched yet (new boxes added from SmartElements,
// offline dev, etc.). Uses a light-background-safe palette so elements are
// always legible on the editor's canvas.  When actual CSS variables arrive via
// cssVariables / themeCssVariables props these defaults are overridden.

export const DEFAULT_CSS_VARIABLES = {
  // Primitives
  "--indigo-900": "hsl(245, 45%, 12%)",
  "--indigo-700": "hsl(245, 55%, 32%)",
  "--indigo-600": "hsl(245, 65%, 48%)",
  "--indigo-400": "hsl(245, 80%, 68%)",
  "--indigo-100": "hsl(245, 60%, 92%)",
  "--gray-950": "hsl(220, 20%,  6%)",
  "--gray-900": "hsl(220, 16%, 10%)",
  "--gray-700": "hsl(220, 10%, 30%)",
  "--gray-400": "hsl(220,  8%, 56%)",
  "--gray-100": "hsl(220, 14%, 94%)",
  "--white": "hsl(0, 0%, 100%)",
  "--black": "hsl(220, 20%,  4%)",
  // Spacing
  "--space-1": "0.2em",
  "--space-2": "0.4em",
  "--space-3": "0.6em",
  "--space-4": "0.75em",
  "--space-6": "1em",
  "--space-8": "1.5em",
  "--space-10": "2em",
  "--space-12": "2.5em",
  // Typography scale
  "--text-xs": "0.6em",
  "--text-sm": "0.72em",
  "--text-base": "0.85em",
  "--text-md": "1em",
  "--text-lg": "1.2em",
  "--text-xl": "1.5em",
  "--text-2xl": "1.875em",
  "--text-3xl": "2.25em",
  "--text-4xl": "2.75em",
  // Radii
  "--radius-sm": "6px",
  "--radius-md": "10px",
  "--radius-lg": "16px",
  "--radius-xl": "24px",
  "--radius-full": "9999px",
  "--stroke-thin": "1px",
  // Semantic color tokens — light-background-safe defaults
  "--color-bg-base": "hsl(0, 0%, 98%)",
  "--color-bg-surface": "rgba(0,0,0,0.04)",
  "--color-bg-surface-strong": "rgba(0,0,0,0.08)",
  "--color-bg-accent": "hsl(245, 65%, 48%)",
  "--color-text-primary": "hsl(220, 16%, 10%)",
  "--color-text-secondary": "hsl(220, 10%, 30%)",
  "--color-text-muted": "hsl(220, 8%, 50%)",
  "--color-text-accent": "hsl(245, 65%, 48%)",
  "--color-border-subtle": "rgba(0,0,0,0.1)",
  "--color-divider": "rgba(0,0,0,0.12)",
  // Typography
  "--font-heading": "Inter, system-ui, sans-serif",
  "--font-body": "Inter, system-ui, sans-serif",
  "--font-heading-weight": "700",
  "--font-display-weight": "800",
  // Surface
  "--surface-radius": "8px",
  "--surface-padding-compact": "0.6em",
  "--surface-padding": "1em",
};

// ─── Context ──────────────────────────────────────────────────────────────────

const ThemeContext = createContext(DEFAULT_CSS_VARIABLES);

export function useTheme() {
  return useContext(ThemeContext);
}

// ─── Provider ────────────────────────────────────────────────────────────────

/**
 * @param {object}    cssVariables  - Map of CSS custom properties from middleware
 * @param {ReactNode} children
 */
export function ThemeBridge({ cssVariables, themeCssVariables, fontScale, children }) {
  // Canvas-level theme variables (from Redux viewToolbar) take priority over
  // the stale per-box variables cached at creation time.
  // Falls back to box-level cssVariables, then to built-in defaults.
  const activeVariables =
    themeCssVariables && Object.keys(themeCssVariables).length > 0
      ? themeCssVariables
      : cssVariables;

  const base =
    activeVariables && Object.keys(activeVariables).length > 0
      ? { ...DEFAULT_CSS_VARIABLES, ...activeVariables }
      : DEFAULT_CSS_VARIABLES;

  // Cap large font and spacing tokens so content fits inside canvas boxes,
  // and normalize any incoming `rem` values to `em` so they scale with the
  // canvas's dynamic font-base (rem is anchored to <html> and never shrinks).
  const merged = (() => {
    const caps = {
      "--text-xl": 1.5,
      "--text-2xl": 1.875,
      "--text-3xl": 2.25,
      "--text-4xl": 2.75,
      "--space-4": 0.75,
      "--space-6": 1,
      "--space-8": 1.5,
      "--space-10": 2,
      "--space-12": 2.5,
      "--surface-padding": 1,
      "--surface-padding-compact": 0.6,
    };
    const scalableKeys = new Set([
      "--text-xs",
      "--text-sm",
      "--text-base",
      "--text-md",
      "--text-lg",
      "--text-xl",
      "--text-2xl",
      "--text-3xl",
      "--text-4xl",
      "--space-1",
      "--space-2",
      "--space-3",
      "--space-4",
      "--space-6",
      "--space-8",
      "--space-10",
      "--space-12",
      "--surface-padding",
      "--surface-padding-compact",
    ]);
    const result = { ...base };
    for (const key of scalableKeys) {
      if (!result[key]) continue;
      const m = /^([0-9.]+)(rem|em)$/.exec(result[key]);
      if (!m) continue;
      let num = parseFloat(m[1]);
      const cap = caps[key];
      if (cap != null && num > cap) num = cap;
      result[key] = `${num}em`;
    }
    // The scene's course font (when one is chosen) wins over the theme's.
    for (const key of ["--font-heading", "--font-body"]) {
      if (result[key]) result[key] = withCourseFont(result[key]);
    }
    return result;
  })();

  // Apply per-box font scale set by the user via the "A"/"a" buttons in EditorShortcuts.
  const scale = fontScale && fontScale !== 1 ? fontScale : null;
  const scaled = scale
    ? (() => {
        const textKeys = [
          "--text-xs",
          "--text-sm",
          "--text-base",
          "--text-md",
          "--text-lg",
          "--text-xl",
          "--text-2xl",
          "--text-3xl",
          "--text-4xl",
        ];
        const r = { ...merged };
        for (const key of textKeys) {
          if (r[key]) {
            const m = /^([0-9.]+)(rem|em|px)$/.exec(r[key]);
            if (m) r[key] = `${(parseFloat(m[1]) * scale).toFixed(3)}${m[2]}`;
          }
        }
        return r;
      })()
    : merged;

  return (
    <ThemeContext.Provider value={scaled}>
      <div
        className="atomic-theme-bridge"
        style={{
          ...scaled,
          // Ensure font tokens cascade to all child components
          fontFamily: scaled["--font-body"],
          color: scaled["--color-text-primary"],
          width: "100%",
          height: "100%",
          boxSizing: "border-box",
        }}
      >
        {children}
      </div>
    </ThemeContext.Provider>
  );
}

export default ThemeBridge;
